import type { Language } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/translations';
import { formatFixedNumber } from '@/utils/numberFormat';

type TranslationValues = Record<string, string | number>;
type Translate = (key: TranslationKey, values?: TranslationValues) => string;

const STATUS_KEYS: Record<string, TranslationKey> = {
  CALCULATED: 'centrifuge.statusCalculated',
  CALCULATED_WITH_WARNINGS: 'centrifuge.statusCalculatedWithWarnings',
  CALCULATED_WITHOUT_DRYING: 'centrifuge.statusCalculatedWithoutDrying',
  INVALID_BALANCE: 'centrifuge.statusFailed',
};

const WARNING_ROLE_KEYS: Record<string, TranslationKey> = {
  'LS Suction': 'centrifuge.role.ls_suction',
  'LS Effluent': 'centrifuge.role.ls_effluent',
  'Drying Effluent': 'centrifuge.role.drying_effluent',
  'HS Effluent': 'centrifuge.role.hs_effluent',
  'Centrifuge 1 Suction': 'centrifuge.role.centrifuge_1_suction',
  'Centrifuge 1 Effluent': 'centrifuge.role.centrifuge_1_effluent',
  'Centrifuge 2 Suction': 'centrifuge.role.centrifuge_2_suction',
  'Centrifuge 2 Effluent': 'centrifuge.role.centrifuge_2_effluent',
};

function warningRoleLabel(role: string, t: Translate): string {
  const key = WARNING_ROLE_KEYS[role];
  return key ? t(key) : role;
}

export function centrifugeStatusLabel(status: string, t: Translate): string {
  const normalizedStatus = status.toUpperCase();
  const key = STATUS_KEYS[normalizedStatus];
  return key ? t(key) : normalizedStatus.replace(/_/g, ' ');
}

export function localizeCentrifugeWarning(
  warning: string,
  language: Language,
  t: Translate
): string {
  if (language !== 'es') return warning;

  const componentClosure = warning.match(
    /^(.+) component closure is (-?\d+(?:\.\d+)?)%\.$/
  );
  if (componentClosure) {
    return t('centrifuge.warning.componentClosure', {
      role: warningRoleLabel(componentClosure[1], t),
      value: formatFixedNumber(Number(componentClosure[2]), language, 1),
    });
  }

  const failedQaqc = warning.match(
    /^(.+) failed QA\/QC but is included in this performance run calculation\.$/
  );
  if (failedQaqc) {
    return t('centrifuge.warning.failedQaqc', {
      role: warningRoleLabel(failedQaqc[1], t),
    });
  }

  const warningQaqc = warning.match(/^(.+) has a QA\/QC warning\.$/);
  if (warningQaqc) {
    return t('centrifuge.warning.qaqc', {
      role: warningRoleLabel(warningQaqc[1], t),
    });
  }

  const densityWarning = warning.match(
    /^(Centrifuge [12] Effluent) density did not decrease from suction\.$/
  );
  if (densityWarning) {
    return t('centrifuge.warning.densityNotDecreased', {
      role: warningRoleLabel(densityWarning[1], t),
    });
  }

  const lgsWarning = warning.match(
    /^(Centrifuge [12] Effluent) LGS did not decrease from suction\.$/
  );
  if (lgsWarning) {
    return t('centrifuge.warning.lgsNotDecreased', {
      role: warningRoleLabel(lgsWarning[1], t),
    });
  }

  const excludedRetake = warning.match(/^(.+) retake was excluded: (.+)$/);
  if (excludedRetake) {
    return t('centrifuge.warning.retakeExcluded', {
      role: warningRoleLabel(excludedRetake[1], t),
      reason: localizeCentrifugeWarning(excludedRetake[2], language, t),
    });
  }

  return warning;
}
